// ---------- General Rules (All States) -------------------------

import { get, getStr, isEmail, isPhone, isYes } from '@shared';

// R1: Contact information format validation
const ruleContactInformationFormat: Rule = {
  id: 'contact.information.format',
  check: (form): RuleResult => {
    const email = getStr(form, 'email');
    const phone = getStr(form, 'telephoneNumber');
    const errs: string[] = [];
    const refs: string[] = [];

    if (email && !isEmail(email)) {
      errs.push('Email address format looks invalid.');
      refs.push('email');
    }

    if (phone && !isPhone(phone)) {
      errs.push('Telephone number format looks invalid.');
      refs.push('telephoneNumber');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CONTACT_INFO_FORMAT',
        message: errs.join(' '),
        refs,
        category: 'formatting',
      };
    }

    return { ok: true };
  },
};

// R2: Weight loss details required
const ruleWeightLossDetailsRequired: Rule = {
  id: 'weight.loss.details.required',
  when: form => isYes(get(form, 'weightLoss')),
  check: (form): RuleResult => {
    const numPounds = getStr(form, 'numPounds');
    const reason = getStr(form, 'reasonWeightLoss');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!numPounds) {
      errs.push('Number of pounds lost is required.');
      refs.push('numPounds');
    }

    if (!reason) {
      errs.push('Reason for weight loss is required.');
      refs.push('reasonWeightLoss');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'WEIGHT_LOSS_DETAILS_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'weightLoss'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R3: Medical marijuana condition required
const ruleMedicalMarijuanaConditionRequired: Rule = {
  id: 'medical.marijuana.condition.required',
  when: form => isYes(get(form, 'medicalMarijuana')),
  check: (form): RuleResult => {
    const condition = getStr(form, 'medicalMarijuanaCondition');

    if (!condition) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'MEDICAL_MARIJUANA_CONDITION_REQUIRED',
        message: 'Medical condition for marijuana use is required.',
        refs: ['medicalMarijuanaCondition', 'medicalMarijuana'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R4: Previously declined details required
const rulePreviouslyDeclinedDetailsRequired: Rule = {
  id: 'previously.declined.details.required',
  when: form => isYes(get(form, 'previouslyDeclined')),
  check: (form): RuleResult => {
    const details = getStr(form, 'declineDetails');

    if (!details) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'DECLINE_DETAILS_REQUIRED',
        message: 'Date and reason for previous decline is required.',
        refs: ['declineDetails', 'previouslyDeclined'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R5: Previously declined eligibility risk
const rulePreviouslyDeclinedEligibilityRisk: Rule = {
  id: 'previously.declined.eligibility.risk',
  when: form => isYes(get(form, 'previouslyDeclined')),
  check: (_form): RuleResult => {
    return {
      ok: false,
      severity: 'low-confidence',
      code: 'PREVIOUSLY_DECLINED_RISK',
      message:
        'Applicant was previously declined for life or LTC insurance; this may impact underwriting approval.',
      refs: ['previouslyDeclined'],
      category: 'carrier-requirements',
    };
  },
};

// R6: Provider recommendations details required
const ruleProviderRecommendationsDetailsRequired: Rule = {
  id: 'provider.recommendations.details.required',
  when: form => isYes(get(form, 'providerRecommendations')),
  check: (form): RuleResult => {
    const details = getStr(form, 'providerRecommendationDetails');

    if (!details) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PROVIDER_RECOMMENDATION_DETAILS_REQUIRED',
        message: 'Details about declined provider recommendations are required.',
        refs: ['providerRecommendationDetails', 'providerRecommendations'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R7: Hypertension details required
const ruleHypertensionDetailsRequired: Rule = {
  id: 'hypertension.details.required',
  when: form => isYes(get(form, 'hypertension')),
  check: (form): RuleResult => {
    const details = getStr(form, 'hypertensionDetails');

    if (!details) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'HYPERTENSION_DETAILS_REQUIRED',
        message: 'Last blood pressure reading is required when hypertension is indicated.',
        refs: ['hypertensionDetails', 'hypertension'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R8: TIA/Stroke diagnosis date required
const ruleTiaStrokeDiagnosisDateRequired: Rule = {
  id: 'tia.stroke.diagnosis.date.required',
  when: form => isYes(get(form, 'tiaStroke')),
  check: (form): RuleResult => {
    const diagnosisDate = getStr(form, 'tiaStrokeDiagnosisDate');

    if (!diagnosisDate) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'TIA_STROKE_DATE_REQUIRED',
        message: 'Date of diagnosis is required for TIA/Stroke.',
        refs: ['tiaStrokeDiagnosisDate', 'tiaStroke'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R9: TIA/Stroke eligibility risk
const ruleTiaStrokeEligibilityRisk: Rule = {
  id: 'tia.stroke.eligibility.risk',
  when: form => isYes(get(form, 'tiaStroke')),
  check: (): RuleResult => {
    return {
      ok: false,
      severity: 'medium-confidence',
      code: 'TIA_STROKE_RISK',
      message:
        'Applicant has history of TIA/Stroke; this may significantly impact underwriting approval.',
      refs: ['tiaStroke'],
      category: 'carrier-requirements',
    };
  },
};

// R10: Blood clots diagnosis date required
const ruleBloodClotsDiagnosisDateRequired: Rule = {
  id: 'blood.clots.diagnosis.date.required',
  when: form => isYes(get(form, 'bloodClots')),
  check: (form): RuleResult => {
    const diagnosisDate = getStr(form, 'bloodClotsDiagnosisDate');

    if (!diagnosisDate) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'BLOOD_CLOTS_DATE_REQUIRED',
        message: 'Date of diagnosis is required for blood clots.',
        refs: ['bloodClotsDiagnosisDate', 'bloodClots'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R11: Diabetes Type 2 A1C required
const ruleDiabetesA1cRequired: Rule = {
  id: 'diabetes.a1c.required',
  when: form => isYes(get(form, 'diabetesType2')),
  check: (form): RuleResult => {
    const a1c = getStr(form, 'diabetesA1c');
    const complications = get(form, 'diabetesComplications');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!a1c) {
      errs.push('A1C level is required for diabetes type 2.');
      refs.push('diabetesA1c');
    }

    if (!complications && complications !== false) {
      errs.push('Diabetes complications status is required.');
      refs.push('diabetesComplications');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'DIABETES_DETAILS_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'diabetesType2'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R12: Diabetes with complications eligibility risk
const ruleDiabetesComplicationsEligibilityRisk: Rule = {
  id: 'diabetes.complications.eligibility.risk',
  when: form => isYes(get(form, 'diabetesType2')) && isYes(get(form, 'diabetesComplications')),
  check: (): RuleResult => {
    return {
      ok: false,
      severity: 'medium-confidence',
      code: 'DIABETES_COMPLICATIONS_RISK',
      message:
        'Applicant has diabetes type 2 with complications (nephropathy, neuropathy, retinopathy, foot sores/ulcers); this significantly impacts underwriting approval.',
      refs: ['diabetesType2', 'diabetesComplications'],
      category: 'carrier-requirements',
    };
  },
};

// R13: Arthritis type required
const ruleArthritisTypeRequired: Rule = {
  id: 'arthritis.type.required',
  when: form => isYes(get(form, 'arthritis')),
  check: (form): RuleResult => {
    const type = getStr(form, 'arthritisType');

    if (!type) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'ARTHRITIS_TYPE_REQUIRED',
        message: 'Arthritis type is required.',
        refs: ['arthritisType', 'arthritis'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R14: Cancer details required
const ruleCancerDetailsRequired: Rule = {
  id: 'cancer.details.required',
  when: form => isYes(get(form, 'cancerHistory')),
  check: (form): RuleResult => {
    const details = getStr(form, 'cancerDetails');

    if (!details) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CANCER_DETAILS_REQUIRED',
        message: 'Cancer details (type, date, treatment, stage/grade) are required.',
        refs: ['cancerDetails', 'cancerHistory'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R15: Cancer history eligibility risk
const ruleCancerHistoryEligibilityRisk: Rule = {
  id: 'cancer.history.eligibility.risk',
  when: form => isYes(get(form, 'cancerHistory')),
  check: (): RuleResult => {
    return {
      ok: false,
      severity: 'high-confidence',
      code: 'CANCER_HISTORY_RISK',
      message:
        'Applicant has cancer history; this may significantly impact underwriting approval depending on type, stage, and time since treatment.',
      refs: ['cancerHistory'],
      category: 'carrier-requirements',
    };
  },
};

// R16: Depression/Anxiety onset date required
const ruleDepressionOnsetDateRequired: Rule = {
  id: 'depression.onset.date.required',
  when: form => isYes(get(form, 'depressionAnxiety')),
  check: (form): RuleResult => {
    const onsetDate = getStr(form, 'depressionOnsetDate');

    if (!onsetDate) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'DEPRESSION_ONSET_DATE_REQUIRED',
        message: 'Date of onset is required for depression/anxiety.',
        refs: ['depressionOnsetDate', 'depressionAnxiety'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R17: Assistive devices explanation required
const ruleAssistiveDevicesExplainRequired: Rule = {
  id: 'assistive.devices.explain.required',
  when: form => isYes(get(form, 'assistiveDevices')),
  check: (form): RuleResult => {
    const explain = getStr(form, 'assistiveDevicesExplain');

    if (!explain) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'ASSISTIVE_DEVICES_EXPLAIN_REQUIRED',
        message: 'Explanation of assistive devices used is required.',
        refs: ['assistiveDevicesExplain', 'assistiveDevices'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R18: Assistive devices eligibility risk
const ruleAssistiveDevicesEligibilityRisk: Rule = {
  id: 'assistive.devices.eligibility.risk',
  when: form => isYes(get(form, 'assistiveDevices')),
  check: (): RuleResult => {
    return {
      ok: false,
      severity: 'medium-confidence',
      code: 'ASSISTIVE_DEVICES_RISK',
      message:
        'Applicant uses assistive devices for mobility; this typically indicates an automatic decline per Nationwide guidelines.',
      refs: ['assistiveDevices'],
      category: 'carrier-requirements',
    };
  },
};

// R19: Memory loss explanation required
const ruleMemoryLossExplainRequired: Rule = {
  id: 'memory.loss.explain.required',
  when: form => isYes(get(form, 'memoryLoss')),
  check: (form): RuleResult => {
    const explain = getStr(form, 'memoryLossExplain');
    const physician = get(form, 'memoryLossPhysician');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!explain) {
      errs.push('Explanation of memory loss is required.');
      refs.push('memoryLossExplain');
    }

    if (!physician && physician !== false) {
      errs.push('Whether memory loss was discussed with physician is required.');
      refs.push('memoryLossPhysician');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'MEMORY_LOSS_DETAILS_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'memoryLoss'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R20: Memory loss eligibility risk
const ruleMemoryLossEligibilityRisk: Rule = {
  id: 'memory.loss.eligibility.risk',
  when: form => isYes(get(form, 'memoryLoss')),
  check: (): RuleResult => {
    return {
      ok: false,
      severity: 'high-confidence',
      code: 'MEMORY_LOSS_RISK',
      message:
        'Applicant has memory loss or cognitive concerns; this typically indicates an automatic decline per Nationwide guidelines.',
      refs: ['memoryLoss'],
      category: 'carrier-requirements',
    };
  },
};

// R21: Physical therapy explanation required
const rulePhysicalTherapyExplainRequired: Rule = {
  id: 'physical.therapy.explain.required',
  when: form => isYes(get(form, 'physicalTherapy')),
  check: (form): RuleResult => {
    const explain = getStr(form, 'physicalTherapyExplain');

    if (!explain) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PHYSICAL_THERAPY_EXPLAIN_REQUIRED',
        message: 'Explanation of physical therapy is required.',
        refs: ['physicalTherapyExplain', 'physicalTherapy'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R22: Injections explanation required
const ruleInjectionsExplainRequired: Rule = {
  id: 'injections.explain.required',
  when: form => isYes(get(form, 'injections')),
  check: (form): RuleResult => {
    const explain = getStr(form, 'injectionsExplain');

    if (!explain) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'INJECTIONS_EXPLAIN_REQUIRED',
        message: 'Explanation of injections is required.',
        refs: ['injectionsExplain', 'injections'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R23: Surgeries explanation required
const ruleSurgeriesExplainRequired: Rule = {
  id: 'surgeries.explain.required',
  when: form => isYes(get(form, 'surgeries')),
  check: (form): RuleResult => {
    const explain = getStr(form, 'surgeriesExplain');

    if (!explain) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'SURGERIES_EXPLAIN_REQUIRED',
        message: 'Explanation of surgeries is required.',
        refs: ['surgeriesExplain', 'surgeries'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R24: Planned surgery explanation required
const rulePlannedSurgeryExplainRequired: Rule = {
  id: 'planned.surgery.explain.required',
  when: form => isYes(get(form, 'plannedSurgery')),
  check: (form): RuleResult => {
    const explain = getStr(form, 'plannedSurgeryExplain');

    if (!explain) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PLANNED_SURGERY_EXPLAIN_REQUIRED',
        message: 'Explanation of planned surgery is required.',
        refs: ['plannedSurgeryExplain', 'plannedSurgery'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R25: Planned surgery eligibility risk
const rulePlannedSurgeryEligibilityRisk: Rule = {
  id: 'planned.surgery.eligibility.risk',
  when: form => isYes(get(form, 'plannedSurgery')),
  check: (): RuleResult => {
    return {
      ok: false,
      severity: 'medium-confidence',
      code: 'PLANNED_SURGERY_ADVISORY',
      message:
        'Applicant has planned or recommended surgery in next 12 months; application may be delayed until after surgery completion.',
      refs: ['plannedSurgery'],
      category: 'carrier-requirements',
    };
  },
};

// R26: Hospitalizations explanation required
const ruleHospitalizationsExplainRequired: Rule = {
  id: 'hospitalizations.explain.required',
  when: form => isYes(get(form, 'hospitalizations')),
  check: (form): RuleResult => {
    const explain = getStr(form, 'hospitalizationsExplain');

    if (!explain) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'HOSPITALIZATIONS_EXPLAIN_REQUIRED',
        message: 'Explanation of hospitalizations is required.',
        refs: ['hospitalizationsExplain', 'hospitalizations'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R27: Weekly activities eligibility advisory
const ruleWeeklyActivitiesAdvisory: Rule = {
  id: 'weekly.activities.advisory',
  when: form => getStr(form, 'weeklyActivities') === '0-1-times',
  check: (): RuleResult => {
    return {
      ok: false,
      severity: 'medium-confidence',
      code: 'WEEKLY_ACTIVITIES_LOW',
      message:
        'Applicant leaves home 0-1 times per week; this may indicate limited mobility or independence concerns.',
      refs: ['weeklyActivities'],
      category: 'carrier-requirements',
    };
  },
};

// ---------- EXPORT ------------------------------------------------------------

export const nationwideUnderwritingAllStatesRules: Rule[] = [
  ruleContactInformationFormat,
  ruleWeightLossDetailsRequired,
  ruleMedicalMarijuanaConditionRequired,
  rulePreviouslyDeclinedDetailsRequired,
  rulePreviouslyDeclinedEligibilityRisk,
  ruleProviderRecommendationsDetailsRequired,
  ruleHypertensionDetailsRequired,
  ruleTiaStrokeDiagnosisDateRequired,
  ruleTiaStrokeEligibilityRisk,
  ruleBloodClotsDiagnosisDateRequired,
  ruleDiabetesA1cRequired,
  ruleDiabetesComplicationsEligibilityRisk,
  ruleArthritisTypeRequired,
  ruleCancerDetailsRequired,
  ruleCancerHistoryEligibilityRisk,
  ruleDepressionOnsetDateRequired,
  ruleAssistiveDevicesExplainRequired,
  ruleAssistiveDevicesEligibilityRisk,
  ruleMemoryLossExplainRequired,
  ruleMemoryLossEligibilityRisk,
  rulePhysicalTherapyExplainRequired,
  ruleInjectionsExplainRequired,
  ruleSurgeriesExplainRequired,
  rulePlannedSurgeryExplainRequired,
  rulePlannedSurgeryEligibilityRisk,
  ruleHospitalizationsExplainRequired,
  ruleWeeklyActivitiesAdvisory,
];

export function evaluateNWUnderwritingNigo(form: PolicyFormSubmissionData): RuleResult[] {
  return nationwideUnderwritingAllStatesRules
    .filter(r => (r.when ? r.when(form) : true))
    .map(r => {
      try {
        return r.check(form);
      } catch (e) {
        return {
          ok: false,
          severity: 'high-confidence' as const,
          code: 'RULE_RUNTIME',
          message: `Rule ${r.id} failed: ${String(e)}`,
          category: 'general' as const,
        };
      }
    })
    .filter(res => !res.ok);
}
